// Voice input for the Quick Counter Memo — Web Speech API only, no external
// library. Works in Chrome/Edge (desktop + Android); Safari/Firefox don't
// support SpeechRecognition, so isVoiceSupported gates everything and the
// typed form always keeps working regardless.
import { useRef, useState, useCallback, useEffect } from 'react';

const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
export const isVoiceSupported = !!SR;

const speak = (text, muted) => {
  if (muted || typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel(); // don't stack prompts if user talks fast
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 1.05;
  window.speechSynthesis.speak(u);
};

const ERROR_MESSAGES = {
  'not-allowed': 'Microphone permission was blocked — allow it in your browser\'s site settings and reload.',
  'service-not-allowed': 'Microphone permission was blocked — allow it in your browser\'s site settings and reload.',
  'no-speech': null,
  'audio-capture': 'No microphone was found — check it\'s connected and not used by another app.',
  'network': 'Voice recognition needs an internet connection (Chrome sends audio to Google\'s servers) — check your connection.',
  'aborted': null,
};

// "Thinky" is the wake word (matches the Think5Whys brand). Browsers often
// mis-hear invented words, so we match generously — thinky/tinky/think e/thin key.
const WAKE_START = /\b(thinky|tinky|think\s*e|thin\s*key)\s*start\b/i;
const WAKE_SAVE = /\b(thinky|tinky|think\s*e|thin\s*key)\s*save\b/i;
const WAKE_CANCEL = /\b(thinky|tinky|think\s*e|thin\s*key)\s*(cancel|stop)\b/i;

// Chrome's SpeechRecognition frequently delivers a phrase like "Thinky" and
// "Start" as TWO separate result events if the speaker pauses even briefly
// between words — checking each event in isolation against a two-word
// pattern then silently fails forever. This buffer joins the last few
// seconds of speech fragments before testing the wake patterns, so a split
// phrase still matches.
const BUFFER_WINDOW_MS = 4000;

const isSkip = (t) => /\b(skip|none|no|not\s*needed)\b/i.test(t);

// Prompts now name the field explicitly and say what kind of answer is
// expected — read aloud, so someone who can't see the screen still knows
// exactly what to say and in what form.
const FIELD_SEQUENCE = [
  { key: 'type', label: 'Sale or Purchase', prompt: "Sale or Purchase? Just say the word Sale, or the word Purchase.", parse: (t) => (/purchase/i.test(t) ? 'Purchase' : 'Sales') },
  { key: 'itemName', label: 'Item Name', prompt: "Item name — say what you're selling or buying.", parse: (t) => t.trim() },
  { key: 'qty', label: 'Quantity', prompt: "Quantity — say a number, like three. Or say skip if it doesn't apply.", parse: (t) => (isSkip(t) ? '' : (t.match(/\d+(\.\d+)?/)?.[0] || '')) },
  { key: 'unit', label: 'Unit', prompt: "Unit — say something like pieces, kilos, or box. Or say skip.", parse: (t) => (isSkip(t) ? '' : t.trim()), skipIf: (f) => !f.qty },
  { key: 'amount', label: 'Amount', prompt: "Amount in rupees — say a number, like five hundred.", parse: (t) => (t.match(/\d+(\.\d+)?/)?.[0] || '') },
  {
    key: 'paymentType', label: 'Payment Mode', prompt: "Payment mode — say Cash, U P I, Card, Credit, or Free.",
    parse: (t) => {
      const s = t.toLowerCase();
      if (s.includes('upi')) return 'UPI';
      if (s.includes('card')) return 'Card';
      if (s.includes('credit')) return 'Credit';
      if (s.includes('free')) return 'Free';
      return 'Cash';
    },
  },
  { key: 'customerName', label: 'Customer / Supplier', prompt: "Customer or supplier name — say their name. Or say skip.", parse: (t) => (isSkip(t) ? '' : t.trim()) },
  { key: 'customerMobile', label: 'Mobile Number', prompt: "Mobile number — say the digits one after another. Or say skip.", parse: (t) => (isSkip(t) ? '' : t.replace(/\D/g, '')) },
  { key: 'remarks', label: 'Remarks', prompt: "Any remarks? Say a short note now, or say Thinky Save to finish without one.", parse: (t) => (isSkip(t) ? '' : t.trim()) },
];

export const VOICE_FIELD_SEQUENCE = FIELD_SEQUENCE;

// ================= Guided wake-word flow =================
export function useVoiceMemoAssistant({ onFieldUpdate, onSave, onCancel, onWake }) {
  const [armed, setArmed] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [lastHeard, setLastHeard] = useState('');
  const [muted, setMuted] = useState(false);
  const [micError, setMicError] = useState(null);

  const recogRef = useRef(null);
  const dictatingRef = useRef(false);
  const stepIndexRef = useRef(0);
  const formRef = useRef({});
  const mutedRef = useRef(false);
  const sessionIdRef = useRef(0);
  const restartTimerRef = useRef(null);
  const bufferRef = useRef([]); // [{ text, at }] — rolling window of recent speech fragments

  const callbacksRef = useRef({ onFieldUpdate, onSave, onCancel, onWake });
  useEffect(() => {
    callbacksRef.current = { onFieldUpdate, onSave, onCancel, onWake };
  }, [onFieldUpdate, onSave, onCancel, onWake]);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const goToStep = useCallback((idx, formSoFar) => {
    let i = idx;
    while (i < FIELD_SEQUENCE.length - 1 && FIELD_SEQUENCE[i]?.skipIf?.(formSoFar)) i++;
    setStepIndex(i);
    stepIndexRef.current = i;
    if (FIELD_SEQUENCE[i]) speak(FIELD_SEQUENCE[i].prompt, mutedRef.current);
  }, []);

  // Appends the latest fragment to the buffer, drops anything older than
  // the window, and returns the joined text to test wake patterns against.
  const pushToBuffer = (text) => {
    const now = Date.now();
    bufferRef.current = [...bufferRef.current, { text, at: now }].filter((f) => now - f.at <= BUFFER_WINDOW_MS);
    return bufferRef.current.map((f) => f.text).join(' ');
  };

  const clearBuffer = () => { bufferRef.current = []; };

  useEffect(() => {
    if (!isVoiceSupported || !armed) return;

    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = false;
    recog.lang = 'en-IN';

    recog.onstart = () => {
      setMicActive(true);
      setMicError(null);
      // console.log('[MiniERP Voice] microphone active, listening for "Thinky Start"');
    };

    recog.onresult = (e) => {
      const text = e.results[e.results.length - 1][0].transcript.trim();
      setLastHeard(text);
      // console.log('[MiniERP Voice] heard:', text);
      const bufferedText = pushToBuffer(text.toLowerCase());
      const lower = text.toLowerCase();

      if (!dictatingRef.current) {
        // Check the fresh fragment first (fast path), then the rolling
        // buffer (catches a wake phrase split across two speech events).
        if (WAKE_START.test(lower) || WAKE_START.test(bufferedText)) {
          clearBuffer();
          formRef.current = {};
          setDictating(true);
          dictatingRef.current = true;
          callbacksRef.current.onWake?.();
          setStepIndex(0);
          stepIndexRef.current = 0;
          speak('Hi! ' + FIELD_SEQUENCE[0].prompt, mutedRef.current);
        }
        return;
      }

      if (WAKE_SAVE.test(lower) || WAKE_SAVE.test(bufferedText)) {
        clearBuffer();
        setDictating(false);
        dictatingRef.current = false;
        speak('Saving now.', mutedRef.current);
        callbacksRef.current.onSave?.();
        return;
      }
      if (WAKE_CANCEL.test(lower) || WAKE_CANCEL.test(bufferedText)) {
        clearBuffer();
        setDictating(false);
        dictatingRef.current = false;
        speak('Cancelled.', mutedRef.current);
        callbacksRef.current.onCancel?.();
        return;
      }

      const step = FIELD_SEQUENCE[stepIndexRef.current];
      if (!step) return;
      const value = step.parse(text);
      formRef.current = { ...formRef.current, [step.key]: value };
      callbacksRef.current.onFieldUpdate?.(step.key, value);
      goToStep(stepIndexRef.current + 1, formRef.current);
    };

    recog.onerror = (e) => {
      console.warn('[MiniERP Voice] recognition error:', e.error);
      const msg = ERROR_MESSAGES[e.error];
      if (msg) {
        setMicError(msg);
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          setArmed(false);
        }
      }
    };

    recog.onend = () => {
      setMicActive(false);
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      restartTimerRef.current = setTimeout(() => {
        try { recog.start(); } catch (err) { console.warn('[MiniERP Voice] restart failed:', err.message); }
      }, 300);
    };

    recogRef.current = recog;
    try { recog.start(); } catch (err) { console.warn('[MiniERP Voice] initial start failed:', err.message); }

    return () => {
      sessionIdRef.current++;
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      recog.onstart = null;
      recog.onresult = null;
      recog.onerror = null;
      recog.onend = null;
      try { recog.stop(); } catch (err) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed]);

  const arm = () => { setMicError(null); clearBuffer(); setArmed(true); };
  const disarm = () => {
    setArmed(false);
    setDictating(false);
    dictatingRef.current = false;
    setMicActive(false);
    clearBuffer();
  };

  return {
    isSupported: isVoiceSupported,
    armed, micActive, dictating, stepIndex, lastHeard, micError,
    currentField: dictating ? FIELD_SEQUENCE[stepIndex] : null,
    totalFields: FIELD_SEQUENCE.length,
    muted, setMuted,
    arm, disarm,
  };
}

// ================= One-shot mic button for a single field =================
export function useSingleFieldDictation() {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);

  const startListening = useCallback((onResult) => {
    if (!isVoiceSupported) return;
    setError(null);
    const recog = new SR();
    recog.continuous = false;
    recog.interimResults = false;
    recog.lang = 'en-IN';
    recog.onresult = (e) => onResult(e.results[0][0].transcript.trim());
    recog.onend = () => setListening(false);
    recog.onerror = (e) => {
      setListening(false);
      const msg = ERROR_MESSAGES[e.error];
      if (msg) setError(msg);
      console.warn('[MiniERP Voice] single-field dictation error:', e.error);
    };
    setListening(true);
    try { recog.start(); } catch (err) { setListening(false); console.warn('[MiniERP Voice] single-field start failed:', err.message); }
  }, []);

  return { listening, startListening, isSupported: isVoiceSupported, error };
}